import { Injectable } from '@angular/core';
import { ActiveToast } from 'ngx-toastr';

import { ToastService } from './toast.service';
import { BrowserToastService } from './browser-toast.service';
import { SingleToastService } from './single-toast.service';

/**
 * Default -> success
 */
type ToastBrowserSeverity = 'success' | 'error' | 'info';

type ToastFacadeOptions = {
  type?: 'mobile' | 'browser';
  /**
   * Default -> simple
   */
  mobileMode?: 'persistent' | 'simple' | 'single' | 'single-persistent';
  duration?: number;
  /**
   * Default -> simple
   */
  browserMode?: 'persistent' | 'simple';
  /**
   * Only for browser
   */
  severity?: ToastBrowserSeverity;
};

@Injectable({
  providedIn: 'root',
})
export class ToastFacade {
  private options: ToastFacadeOptions = {
    type: 'mobile',
    duration: 3000,
    mobileMode: 'simple',
  };

  constructor(
    private readonly browserToastService: BrowserToastService,
    private readonly singleToastService: SingleToastService,
    private readonly toastController: ToastService,
  ) { }

  setToastOptions(options: Partial<ToastFacadeOptions>): void {
    this.options = { ...this.options, ...options };
  }

  present(message: string, title?: string, options?: ToastFacadeOptions): Promise<void | ActiveToast<any> | HTMLIonToastElement> {
    const toastOptions = { ...options, ...this.options }
    switch ( toastOptions.type ) {
      case 'browser':
        return this.browserPresent(toastOptions, message, title);
      default:
      case 'mobile':
        return this.mobilePresent(toastOptions, message, title);
    }
  }

  mobilePresent(options: ToastFacadeOptions, message: string, title?: string): Promise<void | HTMLIonToastElement> {
    switch ( options.mobileMode ) {
      case 'persistent':
        return this.toastController.persistence(message, title);
      case 'single':
        return this.singleToastService.enqueueToast({ message, header: title });
      case 'single-persistent':
        return this.singleToastService.enqueueToast({ message, header: title }, true);
      default:
      case 'simple':
        return this.toastController.simple(message, options.duration, title);
    }
  }

  async browserPresent(options: ToastFacadeOptions, message: string, title?: string): Promise<ActiveToast<any>> {
    switch ( options.browserMode ) {
      case 'persistent':
        return this.browserPersistentPresent(options.severity || 'success', message, title);
      default:
      case 'simple':
        return this.browserSimplePresent(options.severity || 'success', message, title);
    }
  }

  async browserPersistentPresent(severity: ToastBrowserSeverity, message: string, title?: string): Promise<ActiveToast<any>> {
    switch ( severity ) {
      case 'error':
        return this.browserToastService.persistanceError(message, title);
      case 'info':
        return this.browserToastService.persistanceInfo(message, title);
      default:
      case 'success':
        return this.browserToastService.persistanceSuccess(message, title);
    }
  }

  async browserSimplePresent(severity: ToastBrowserSeverity, message: string, title?: string): Promise<ActiveToast<any>> {
    switch ( severity ) {
      case 'error':
        return this.browserToastService.simpleError(message, title);
      case 'info':
        return this.browserToastService.simpleInfo(message, title);
      default:
      case 'success':
        return this.browserToastService.simpleSuccess(message, title);
    }
  }
}
